import React, { useEffect, useState } from 'react';
import { useAddStock } from '../../hooks/useInventory';
import { Product, AddStockDto } from '../../services/inventory.service';
import { Modal } from '../ui/Modal';
import {
  Field,
  FormActions,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import ProductStockSummary from './ProductStockSummary';

interface AddStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
}

const stockReasons = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'production', label: 'Production' },
  { value: 'adjustment_in', label: 'Adjustment In' },
  { value: 'return', label: 'Return' },
  { value: 'transfer_in', label: 'Transfer In' },
  { value: 'initial_stock', label: 'Initial Stock' },
];

const emptyForm: AddStockDto = {
  quantity: 0,
  reason: 'purchase',
  unitCost: 0,
  referenceId: '',
  referenceType: '',
  notes: '',
};

const AddStockModal: React.FC<AddStockModalProps> = ({ isOpen, onClose, product }) => {
  const [formData, setFormData] = useState<AddStockDto>(emptyForm);
  const addStockMutation = useAddStock();

  // Start from a clean form every time the dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData(emptyForm);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product) return;

    try {
      await addStockMutation.mutateAsync({
        id: product.id,
        data: {
          ...formData,
          unitCost: formData.unitCost || undefined,
          referenceId: formData.referenceId || undefined,
          referenceType: formData.referenceType || undefined,
          notes: formData.notes || undefined,
        },
      });
      onClose();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  if (!product) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Stock"
      description="Record stock coming in for this product."
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <ProductStockSummary
          product={product}
          newStock={Number(product.currentStock) + (formData.quantity || 0)}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Quantity to Add" htmlFor="add-stock-quantity" required>
            <input
              type="number"
              id="add-stock-quantity"
              min="0"
              step="0.0001"
              required
              value={formData.quantity || ''}
              onChange={(e) => setFormData({ ...formData, quantity: Math.round((parseFloat(e.target.value) || 0) * 10000) / 10000 })}
              className={inputClass}
              placeholder="0"
            />
          </Field>

          <Field label="Reason" htmlFor="add-stock-reason" required>
            <select
              id="add-stock-reason"
              required
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className={inputClass}
            >
              {stockReasons.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Unit Cost (₹)" htmlFor="add-stock-unit-cost" hint="Optional. Used to value this receipt.">
            <input
              type="number"
              id="add-stock-unit-cost"
              min="0"
              step="0.01"
              value={formData.unitCost || ''}
              onChange={(e) => setFormData({ ...formData, unitCost: parseFloat(e.target.value) || 0 })}
              className={inputClass}
              placeholder="0.00"
            />
          </Field>

          <Field label="Reference ID" htmlFor="add-stock-reference-id">
            <input
              type="text"
              id="add-stock-reference-id"
              value={formData.referenceId}
              onChange={(e) => setFormData({ ...formData, referenceId: e.target.value })}
              className={inputClass}
              placeholder="e.g. PO-2024-001"
            />
          </Field>

          <Field label="Reference Type" htmlFor="add-stock-reference-type" className="sm:col-span-2">
            <input
              type="text"
              id="add-stock-reference-type"
              value={formData.referenceType}
              onChange={(e) => setFormData({ ...formData, referenceType: e.target.value })}
              className={inputClass}
              placeholder="e.g. purchase_order, production_order"
            />
          </Field>

          <Field label="Notes" htmlFor="add-stock-notes" className="sm:col-span-2">
            <textarea
              id="add-stock-notes"
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className={inputClass}
              placeholder="Additional notes..."
            />
          </Field>
        </div>

        <FormActions>
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={addStockMutation.isPending || formData.quantity <= 0}
            className={primaryButtonClass}
          >
            {addStockMutation.isPending ? 'Adding...' : 'Add Stock'}
          </button>
        </FormActions>
      </form>
    </Modal>
  );
};

export default AddStockModal;
